import {Component, EventEmitter, Inject, Input, OnChanges, OnInit, Output} from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import {NotificationService} from '../../notification/notification.service';
import {AuthService} from '../../core/security/auth.service';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef, MatDialogTitle, MatDialogClose, MatDialogContent, MatDialogActions } from '@angular/material/dialog';
import {ContactService} from '../contact.service';
import {RolesEnum} from '../../core/security/roles.enum';
import {ModulesEnum} from '../../core/security/modules.enum';
import {SaveContactsRequest} from '../../api/models/save-contacts-request';
import {ContactsRequest} from '../../api/models/contacts-request';
import {TagsGridDialogComponent} from '../../tags/tags-list-dialog.component';
import { BaseComponent } from '../../core/base.component';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { HeaderService } from '../../core/services/header.service';
import { Title } from '@angular/platform-browser';
import { CurrentUser } from '../../api/models/current-user';
import { FlexModule } from '@angular/flex-layout';
import { MatIconButton, MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { NgIf } from '@angular/common';
import { MatCheckboxInputComponent } from '../../editors/mat-checkbox-input/mat-checkbox-input.component';
import { MatTagsMultiselectComponent } from '../../editors/mat-tags-multiselect/mat-tags-multiselect.component';
import { MatRadioGroup, MatRadioButton } from '@angular/material/radio';
import { MatSlideToggleInputComponent } from '../../editors/mat-slide-toggle-input/mat-slide-toggle-input.component';
import { LongTaskButtonComponent } from '../../core/visual/long-task-button/long-task-button.component';

export interface BulkEditDialogData {
  ids: string[];
  totalContacts: number;
  filter?: ContactsRequest;
}

@Component({
    selector: 'app-contacts-bulk-edit',
    templateUrl: './bulk-edit.component.html',
    styleUrls: ['./bulk-edit.component.scss'],
    standalone: true,
    imports: [MatDialogTitle, FlexModule, MatIconButton, MatDialogClose, MatIcon, CdkScrollable, MatDialogContent, FormsModule, ReactiveFormsModule, NgIf, MatCheckboxInputComponent, MatTagsMultiselectComponent, MatRadioGroup, MatRadioButton, MatSlideToggleInputComponent, RouterLink, MatDialogActions, MatButton, LongTaskButtonComponent]
})
export class ContactsBulkEditComponent extends BaseComponent implements OnInit, OnChanges {

  form: UntypedFormGroup;
  deleteEnableControl = new UntypedFormControl(false, []);
  syncWithPbxEnableControl = new UntypedFormControl(false, []);
  tagsEnableControl = new UntypedFormControl(false, []);
  syncWithPbxControl = new UntypedFormControl(false, []);
  tagsControl = new UntypedFormControl([], []);
  tagsMergeTypeControl = new UntypedFormControl('replace', []);
  public submitted: boolean;
  public serverError: boolean;
  buttonSubmitPromise: Promise<void>;

  canEditTags: boolean;
  canEdit: boolean;
  canDeleteCompanyContacts: boolean;
  canDeletePrivateContacts: boolean;
  limitedDeleteAccess: boolean;
  private user?: CurrentUser;
  onConfirm: Promise<void>;
  @Input() asyncConfirm: (filter?: any) => Promise<void>;
  @Output() onSuccess = new EventEmitter<void>();

  totalSelected = 0;
  totalFiltered = 0;

  constructor(@Inject(MAT_DIALOG_DATA) public data: BulkEditDialogData,
              public dialogRef: MatDialogRef<ContactsBulkEditComponent>,
              public dialog: MatDialog,
              public contactService: ContactService,
              private fb: UntypedFormBuilder,
              public readonly route: ActivatedRoute,
              public readonly headerService: HeaderService,
              public readonly titleService: Title,
              public readonly authService: AuthService,
              private notificationService: NotificationService) {
    super(route, headerService, titleService, authService);

    this.canEdit = this.authService.hasAccess([
      RolesEnum.CompanyContact_Edit,
      RolesEnum.CompanyContact_Delete,
      RolesEnum.PrivateContact_Edit,
      RolesEnum.PrivateContact_Delete,
    ]) && this.authService.userHasPackage(ModulesEnum.ContactModule);

    this.canEditTags = this.authService.hasAccess([
      RolesEnum.Tags_Edit,
      RolesEnum.Tags_Delete
    ]) && this.authService.userHasPackage(ModulesEnum.TagsModule);

    this.canDeletePrivateContacts = this.authService.hasAccess([
      RolesEnum.PrivateContact_Delete,
    ]) && this.authService.userHasPackage(ModulesEnum.PrivateContactModule);

    this.canDeleteCompanyContacts = this.authService.hasAccess([
      RolesEnum.CompanyContact_Delete,
    ]) && this.authService.userHasPackage(ModulesEnum.ContactModule);

    this.limitedDeleteAccess = !this.canDeleteCompanyContacts || !this.canDeletePrivateContacts;

    this.createForm();
  }

  onCurrentUserChanged(user?: CurrentUser) {
    console.log('onCurrentUserChanged', user);
    this.user = user;
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.totalSelected = this.data.ids.length;
    this.totalFiltered = this.data.totalContacts;

    this.ngOnChanges();
  }

  selectAllContacts() {
    this.totalSelected = this.totalFiltered;
  }

  createForm(): void {
    this.form = this.fb.group({
        'tags': this.tagsControl,
        'tagsMergeType': this.tagsMergeTypeControl,
        'tagsEnabled': this.tagsEnableControl,
        'syncWithPbxEnabled': this.syncWithPbxEnableControl,
        'syncWithPbx': this.syncWithPbxControl,
        'deleteEnabled': this.deleteEnableControl,
      }
    );
  }

  ngOnChanges() {
    this.submitted = false;
    this.form.reset({
      'tags': [],
      'tagsMergeType': 'replace',
      'tagsEnabled': false,
      'syncWithPbxEnabled': false,
      'syncWithPbx': false,
      'deleteEnabled': false
    });
  }

  async onSubmit() {
    this.submitted = true;
    this.serverError = false;

    if (this.form.valid) {
      //this.buttonSubmitPromise = this.processSubmit();
      await this.processSubmit();
    }
  }

  onManageTagsClick($event: MouseEvent) {
    $event.stopPropagation();
    const dialogRef = this.dialog.open(TagsGridDialogComponent, {
      width: '600px',
      height: '90%',
    });

    dialogRef.afterClosed().subscribe(async () => {
      await this.contactService.getTags();
    });
  }

  async processSubmit(): Promise<void> {
    const reqData = this.prepareFormModel();

    if (await this.contactService.saveContacts(reqData)) {
      this.notificationService.notifySuccess('The changes have been submitted successfully');

      this.dialogRef.close('success');

      this.ngOnChanges();
    } else {
      this.serverError = true;
    }
  }

  prepareFormModel(): SaveContactsRequest {
    const formModel = this.form.value;
    return {
      tags: formModel.tags,
      tagsMergeType: formModel.tagsMergeType,
      ids: this.totalSelected === this.totalFiltered ? [] : this.data.ids,
      updateAll: this.totalSelected === this.totalFiltered,
      filter: this.data.filter,
      syncWithPbx: formModel.syncWithPbx,

      syncWithPbxEnabled: formModel.syncWithPbxEnabled,
      tagsEnabled: formModel.tagsEnabled,
      deleteEnabled: formModel.deleteEnabled,
    };
  }

  onTagsListClose($event: any | Event) {
    this.contactService.getTags().then();
  }

  onOtherEnableChange($event: boolean) {
    console.log(`delete enable change ${$event}`);
    if ($event) {
      this.form.patchValue(
        {
          deleteEnabled: false
        }
      );
    }
  }

  onDeleteEnableChange($event: boolean) {
    console.log(`delete enable change ${$event}`);
    if ($event) {
      this.form.patchValue(
        {
          syncWithPbxEnabled: false,
          tagsEnabled: false
        }
      );
    }
  }

  getTaskKey() {
    return `contacts:${this.user?.customerUserId}`;
  }
}
